import { Icon } from '@arcadiapower/shrike';
import { addQueryParams } from '@arcadiapower/warbler';
import { useEffect, ReactNode, useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '~/components/button';
import { HelpDialog } from '~/components/dialogs';
import { Link } from '~/components/link';
import { Title, Description } from '~/components/typography';
import { UnparsedQueryParams, QueryParamsContext } from '~/providers/query-params';
import { FORGOT_PASSWORD_URL, LOGIN_URL } from '~/utils/constants';
import { store } from '~/utils/store';
import { trackError, trackEvent, TRACK } from '~/utils/tracking';
import { Wrapper } from './error.util';

const RestartLink = ({
  children,
  url = '/',
  params = {},
}: {
  children: ReactNode;
  params?: UnparsedQueryParams;
  url?: string;
}) => {
  const restartUrl = addQueryParams(url, params);
  return (
    <Link
      testId="restart-link"
      href={restartUrl}
      fullWidth
      openInNewTab={false}
      onClick={() => store.clearAll()}
    >
      {children}
    </Link>
  );
};

export const DeletedAccount = () => (
  <Wrapper testId="screen-DeletedAccount">
    <Title textAlign="center" marginTop="48px">
      We couldn’t find your account.
    </Title>
    <div style={{ width: '353px', marginLeft: 'auto', marginRight: 'auto' }}>
      <Description textAlign="center" marginTop="24px">
        Not to worry. Please contact our support team to finish setting up your account
      </Description>
    </div>
    <HelpDialog
      target={
        <Link color="primary" openInNewTab={false}>
          <Button mode="primary">Contact us</Button>
        </Link>
      }
    />
  </Wrapper>
);

export const ExpiredLoginLink = () => (
  <Wrapper testId="screen-ExpiredLoginLink">
    <Title textAlign="center" marginTop="48px">
      Your sign in link expired.
    </Title>
    <div style={{ width: '353px', marginLeft: 'auto', marginRight: 'auto' }}>
      <Description textAlign="center" marginTop="24px">
        Magic sign in links expire after 24 hours, and can only be used once.
      </Description>
    </div>
    <Link
      color="primary"
      openInNewTab={false}
      aria-label="ExpiredLoginLink"
      href={FORGOT_PASSWORD_URL}
    >
      <Button mode="primary">Request a new magic link</Button>
    </Link>
  </Wrapper>
);

export const ExpiredSession = () => {
  const { state } = useLocation();
  return (
    <Wrapper testId="screen-ExpiredSession">
      <Title textAlign="center" marginTop="48px">
        Your session has expired.
      </Title>
      <Description textAlign="center" marginTop="24px">
        Please sign in to finish setting up your account
      </Description>
      <Link color="primary" openInNewTab={false} aria-label="ExpiredSession" href={LOGIN_URL}>
        <Button mode="primary">Sign in</Button>
      </Link>
      <Description textAlign="center" marginTop="24px">
        New to Arcadia?{' '}
        <RestartLink url={state?.url}>
          Sign up
          <Icon color="primary" icon="LinkArrow" style={{ marginLeft: '7px' }}></Icon>
        </RestartLink>
      </Description>
    </Wrapper>
  );
};

export const Friction = () => (
  <Wrapper testId="screen-Friction">
    <Title textAlign="center" marginTop="48px">
      Something went wrong.
    </Title>
    <div style={{ width: '353px', marginLeft: 'auto', marginRight: 'auto' }}>
      <Description textAlign="center" marginTop="24px">
        It looks like you're not eligible for this product. That's okay, we can match you with
        something else. <RestartLink data-testid="Friction">Try again.</RestartLink>
      </Description>
    </div>
  </Wrapper>
);

export const Unexpected = ({ reason }: { reason?: string } = {}) => {
  useEffect(() => {
    trackEvent(TRACK.unexpected);
    trackError(new Error('Unexpected screen shown'), { extras: { reason } });
  }, []);

  const { unparsedQueryParams } = useContext(QueryParamsContext);

  return (
    <Wrapper testId="screen-Unexpected">
      <Title textAlign="center" marginTop="48px">
        Oops, something unexpected happened.
      </Title>
      <div style={{ width: '353px', marginLeft: 'auto', marginRight: 'auto' }}>
        <Description textAlign="center" marginTop="24px">
          Please refresh the page and try again. For further assistance please{' '}
          <HelpDialog
            target={
              <Link color="primary" openInNewTab={false}>
                contact us
              </Link>
            }
          />
        </Description>
      </div>
      <RestartLink params={unparsedQueryParams}>
        <Button mode="primary">Refresh Page</Button>
      </RestartLink>
    </Wrapper>
  );
};
